// Admin data layer for Classrooms.

import { supabase, isSupabaseConfigured } from "../supabase";

function ensureLive() {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
}

export async function listClassrooms({ search } = {}) {
  ensureLive();
  let q = supabase
    .from("classrooms")
    .select(`
      id, name, age_group, capacity, licensed_capacity, location_id, compliance_program_key, monthly_tuition_cents, required_staff_ratio, created_at,
      location:locations(id, name),
      children:children(id, withdrawn_at),
      assignments:staff_assignments(id, profile_id, position, starts_on, ends_on,
        profile:profiles(id, full_name, email))
    `)
    .order("name", { ascending: true });
  if (search) q = q.ilike("name", `%${search}%`);
  const { data, error } = await q;
  if (error) throw error;
  const today = new Date().toISOString().slice(0, 10);
  return (data || []).map((r) => {
    const enrolled = (r.children || []).filter((c) => !c.withdrawn_at).length;
    const activeStaff = (r.assignments || []).filter(
      (a) => !a.ends_on || a.ends_on >= today,
    );
    return {
      ...r,
      enrolled_count: enrolled,
      active_staff: activeStaff,
      active_staff_count: activeStaff.length,
    };
  });
}

export async function getClassroom(id) {
  ensureLive();
  const { data, error } = await supabase
    .from("classrooms")
    .select(`
      id, organization_id, name, age_group, capacity, licensed_capacity, location_id, compliance_program_key, monthly_tuition_cents, required_staff_ratio,
      location:locations(id, name),
      children:children(id, first_name, last_name, withdrawn_at),
      assignments:staff_assignments(id, profile_id, position, starts_on, ends_on,
        profile:profiles(id, full_name, email))
    `)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function createClassroom({ organizationId, name, ageGroup, capacity, monthlyTuitionCents, complianceProgramKey }) {
  ensureLive();
  const { data, error } = await supabase
    .from("classrooms")
    .insert({
      organization_id: organizationId,
      name,
      age_group: ageGroup || null,
      capacity: capacity || null,
      monthly_tuition_cents: monthlyTuitionCents ?? null,
      compliance_program_key: complianceProgramKey || null,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function updateClassroom(id, patch) {
  ensureLive();
  const { data, error } = await supabase
    .from("classrooms")
    .update(patch)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

/**
 * Safe delete: block if children (any, incl. withdrawn) reference it
 * or if there are current/future staff assignments. Uses .select() on
 * the delete to catch RLS-blocked and FK-blocked cases that the
 * pre-check might miss.
 */
export async function deleteClassroom(id) {
  ensureLive();
  const today = new Date().toISOString().slice(0, 10);
  const [children, staff] = await Promise.all([
    supabase.from("children").select("id").eq("classroom_id", id).limit(1),
    supabase
      .from("staff_assignments")
      .select("id,ends_on")
      .eq("classroom_id", id),
  ]);
  if (children.error) throw children.error;
  if (staff.error) throw staff.error;
  if ((children.data || []).length > 0) return { ok: false, reason: "has_children" };
  const futureStaff = (staff.data || []).filter((a) => !a.ends_on || a.ends_on >= today);
  if (futureStaff.length > 0) return { ok: false, reason: "has_staff" };
  const { data, error } = await supabase
    .from("classrooms")
    .delete()
    .eq("id", id)
    .select("id");
  if (error) {
    if (error.code === "23503") return { ok: false, reason: "has_children" };
    throw error;
  }
  if (!data || data.length === 0) return { ok: false, reason: "blocked" };
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Staff assignments (classroom-side helpers)
// ---------------------------------------------------------------------------

export async function assignStaffToClassroom({ organizationId, profileId, classroomId, position, startsOn }) {
  ensureLive();
  const { data, error } = await supabase
    .from("staff_assignments")
    .insert({
      organization_id: organizationId,
      profile_id: profileId,
      classroom_id: classroomId,
      position: position || "lead_teacher",
      starts_on: startsOn || new Date().toISOString().slice(0, 10),
    })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function endStaffAssignment(assignmentId, endsOn) {
  ensureLive();
  const { data, error } = await supabase
    .from("staff_assignments")
    .update({ ends_on: endsOn || new Date().toISOString().slice(0, 10) })
    .eq("id", assignmentId)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function deleteStaffAssignment(assignmentId) {
  ensureLive();
  const { error } = await supabase
    .from("staff_assignments")
    .delete()
    .eq("id", assignmentId);
  if (error) throw error;
}

export const STAFF_POSITIONS = [
  { value: "lead_teacher", label: "Lead teacher" },
  { value: "assistant",    label: "Assistant" },
  { value: "floater",      label: "Floater" },
  { value: "director",     label: "Director" },
];
